import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";

const InterestDialog = ({ profile, onClose }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const canSendInterest = user && !profile.isSample;

  const contact = () => {
    onClose();
    toast(t("Request received — our team will guide you through the next step."), {
      description: `Suffa Matrimony ${profile.id} ${t("will reach out shortly, insha’Allah.")}`,
    });
  };

  const sendInterest = async () => {
    setSending(true);
    try {
      await api.post("/interests", { to_profile_id: profile.id });
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fm-modal-overlay" data-testid="interest-dialog" onClick={onClose}>
      <div
        className="fm-modal fm-modal-sm"
        role="dialog"
        aria-modal="true"
        aria-label={`Interested in profile ${profile.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="fm-modal-close"
          data-testid="interest-dialog-close"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={18} />
        </button>

        <span className="fm-medallion" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
            <g stroke="#C5A059" strokeWidth="1.5" fill="none">
              <rect x="12.5" y="12.5" width="15" height="15" />
              <rect x="12.5" y="12.5" width="15" height="15" transform="rotate(45 20 20)" />
            </g>
            <circle cx="20" cy="20" r="2.6" fill="#C5A059" />
          </svg>
        </span>

        <h3 className="fm-dialog-title" data-testid="interest-dialog-title">
          {t("Interested in this profile?")}
        </h3>

        {sent ? (
          <p className="fm-dialog-text" data-testid="interest-sent-confirmation">
            {t(
              "Your interest has been sent to Suffa Matrimony for review. Once approved, the other family will be informed — you can follow it in My Profile → Interests.",
            )}
          </p>
        ) : canSendInterest ? (
          <p className="fm-dialog-text" data-testid="interest-dialog-text">
            {t(
              "Your interest will first be reviewed by the Suffa Matrimony team. If approved, the other family will be informed. Sending an interest is free.",
            )}
          </p>
        ) : user ? (
          <p className="fm-dialog-text" data-testid="interest-dialog-text">
            {t("Complete your own matrimonial profile to register an interest. Our team will guide you through the next step.")}
          </p>
        ) : (
          <p className="fm-dialog-text" data-testid="interest-dialog-text">
            {t("Login or register with Suffa Matrimony to send an interest, or contact our team and we will guide you through the next step.")}
          </p>
        )}

        <div className="fm-modal-actions">
          {sent ? (
            <a href="/my-profile" className="btn btn-solid btn-lg" data-testid="interest-view-mine">
              {t("My Profile")}
            </a>
          ) : canSendInterest ? (
            <button
              type="button"
              className="btn btn-solid btn-lg"
              data-testid="fm-send-interest"
              disabled={sending}
              onClick={sendInterest}
            >
              {sending ? t("Sending…") : t("Send Interest")}
            </button>
          ) : user ? (
            <a href="/create-profile" className="btn btn-solid btn-lg" data-testid="fm-complete-profile-cta">
              {t("Create Your Profile")}
            </a>
          ) : (
            <a href="/login" className="btn btn-solid btn-lg" data-testid="fm-login-to-interest">
              {t("Login")}
            </a>
          )}
          <button type="button" className="btn btn-outline btn-lg" onClick={onClose}>
            {t("Close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterestDialog;
